import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Avatar,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Skeleton,
    IconButton,
    Modal,
    Tooltip,
    Button,
} from '@mui/material';
import {
    Person as PersonIcon,
    LocationOn as LocationIcon,
    Public as GlobeIcon,
    Wc as GenderIcon,
    Cake as AgeIcon,
    Flag as ReportIcon,
    History as HistoryIcon,
    Gavel as GavelIcon,
    Close as CloseIcon,
    WarningAmberRounded as WarningIcon,
    PhotoLibrary as PhotoIcon,
    Fullscreen as ExpandIcon,
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    CalendarMonth as CalendarIcon,
    Badge as BadgeIcon,
} from '@mui/icons-material';
import { adminApi, UserProfile } from '../../../api/adminApi';
import { adminTokens as T } from '../../../theme/adminTokens';
import EngagementPanel from './EngagementPanel';

interface UserProfileViewProps {
    open: boolean;
    onClose: () => void;
    /** Look the member up by email (reports) or by account ID (User Management; some members have no email) */
    userEmail?: string;
    userId?: string;
}

type Tone = { bg: string; text: string; dot?: string; label: string };

const STATUS_TONE: Record<string, Tone> = {
    active:    { bg: T.color.success.soft, text: T.color.success.text, dot: T.color.success.base, label: 'Active' },
    warned:    { bg: T.color.warning.soft, text: T.color.warning.text, dot: T.color.warning.base, label: 'Warned' },
    suspended: { bg: T.color.warning.soft, text: T.color.warning.text, dot: T.color.warning.base, label: 'Suspended' },
    banned:    { bg: T.color.error.soft,   text: T.color.error.text,   dot: T.color.error.base,   label: 'Banned' },
    pending:   { bg: T.color.warning.soft, text: T.color.warning.text, label: 'Pending' },
    reviewing: { bg: T.color.info.soft,    text: T.color.info.text,    label: 'Reviewing' },
    resolved:  { bg: T.color.success.soft, text: T.color.success.text, label: 'Resolved' },
    dismissed: { bg: T.color.surface.sunken, text: T.color.text.secondary, label: 'Dismissed' },
};
const PRIORITY_TONE: Record<string, Tone> = {
    urgent: { dot: T.color.error.base,   bg: T.color.error.soft,   text: T.color.error.text,   label: 'Urgent' },
    high:   { dot: T.color.warning.base, bg: T.color.warning.soft, text: T.color.warning.text, label: 'High' },
    medium: { dot: T.color.info.base,    bg: T.color.info.soft,    text: T.color.info.text,    label: 'Medium' },
    low:    { dot: T.color.success.base, bg: T.color.success.soft, text: T.color.success.text, label: 'Low' },
};

const formatReportType = (type: string) =>
    (type || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const formatActionType = (type: string) =>
    (type || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const initialsFrom = (name?: string | null, email?: string | null) => {
    const source = (name && name.trim()) || (email ? email.split('@')[0] : '');
    if (!source) return '?';
    const parts = source.split(/[._\s-]/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || source[0].toUpperCase();
};

const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const formatAccountAge = (days?: number) => {
    if (days == null) return '—';
    if (days < 1) return 'Today';
    if (days < 30) return `${days}d`;
    if (days < 365) return `${Math.floor(days / 30)}mo`;
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    return months > 0 ? `${years}y ${months}mo` : `${years}y`;
};

const Pill: React.FC<{ tone: Tone; icon?: React.ReactNode }> = ({ tone, icon }) => (
    <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: `${T.space[1]}px`,
        px: `${T.space[2]}px`, py: `2px`,
        borderRadius: `${T.radius.pill}px`,
        bgcolor: tone.bg, color: tone.text,
        fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
        whiteSpace: 'nowrap',
    }}>
        {tone.dot && !icon && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tone.dot }} />}
        {icon}
        {tone.label}
    </Box>
);

const Panel: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
    <Box sx={{
        bgcolor: T.color.surface.raised,
        border: `1px solid ${T.color.surface.border}`,
        borderRadius: `${T.radius.lg}px`,
        boxShadow: T.shadow.sm,
        p: `${T.space[5]}px`,
        ...sx,
    }}>{children}</Box>
);

const PanelTitle: React.FC<{ icon: React.ReactNode; children: React.ReactNode; count?: number }> = ({ icon, children, count }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[2]}px`, mb: `${T.space[4]}px` }}>
        <Box sx={{ color: T.color.text.tertiary, display: 'flex' }}>{icon}</Box>
        <Typography sx={{ fontSize: T.type.size.lg, fontWeight: T.type.weight.bold, color: T.color.text.primary }}>
            {children}{count != null ? ` (${count})` : ''}
        </Typography>
    </Box>
);

const DetailRow: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: `${T.space[2]}px` }}>
        <Box sx={{ color: T.color.text.tertiary, display: 'flex', mt: '2px' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary }}>{label}</Typography>
            <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.medium, color: T.color.text.primary }}>
                {value}
            </Typography>
        </Box>
    </Box>
);

const StatTile: React.FC<{ label: string; value: React.ReactNode; tone?: 'neutral' | 'warning' | 'error' }> = ({ label, value, tone = 'neutral' }) => {
    const palette = tone === 'error' ? { bg: T.color.error.soft, text: T.color.error.text }
        : tone === 'warning' ? { bg: T.color.warning.soft, text: T.color.warning.text }
        : { bg: T.color.surface.sunken, text: T.color.text.primary };
    return (
        <Box sx={{
            bgcolor: palette.bg, borderRadius: `${T.radius.md}px`,
            p: `${T.space[3]}px`, textAlign: 'center',
        }}>
            <Typography sx={{ fontSize: T.type.size.xl, fontWeight: T.type.weight.bold, color: palette.text, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                {value}
            </Typography>
            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                {label}
            </Typography>
        </Box>
    );
};

const UserProfileView: React.FC<UserProfileViewProps> = ({ open, onClose, userEmail, userId }) => {
    const [userData, setUserData] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Photo viewer state
    const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [photos, setPhotos] = useState<string[]>([]);

    useEffect(() => {
        if (open && (userId || userEmail)) {
            fetchUserData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, userEmail, userId]);

    const fetchUserData = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = userId
                ? await adminApi.getUserProfileById(userId)
                : await adminApi.getUserProfile(userEmail || '');
            setUserData(response.data);

            const userPhotos = response.data?.profile?.photos || [];
            const processedPhotos = userPhotos
                .map((photo: { uri: string } | string) => typeof photo === 'string' ? photo : photo?.uri)
                .filter((uri: string | undefined): uri is string => !!uri);
            setPhotos(processedPhotos);
        } catch (err: any) {
            setError(err.message || 'Failed to load user data');
        } finally {
            setLoading(false);
        }
    };

    const currentStatus = (userData?.profile.current_status || 'active').toLowerCase();
    const isCurrentlyBanned = currentStatus === 'banned';
    const isCurrentlySuspended = currentStatus === 'suspended';

    // Photo viewer handlers
    const handlePhotoClick = (index: number) => {
        setCurrentPhotoIndex(index);
        setPhotoViewerOpen(true);
    };
    const handlePhotoViewerClose = () => setPhotoViewerOpen(false);
    const handlePrevPhoto = useCallback(() => {
        setCurrentPhotoIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
    }, [photos.length]);
    const handleNextPhoto = useCallback(() => {
        setCurrentPhotoIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
    }, [photos.length]);

    // Keyboard navigation for the lightbox
    useEffect(() => {
        if (!photoViewerOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') handlePrevPhoto();
            else if (e.key === 'ArrowRight') handleNextPhoto();
            else if (e.key === 'Escape') handlePhotoViewerClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [photoViewerOpen, handlePrevPhoto, handleNextPhoto]);

    const stats = userData?.stats;
    const profile = userData?.profile;
    const locationText = profile?.location
        ? (typeof profile.location === 'string' ? profile.location : (profile.location as any)?.name || 'Unknown')
        : null;
    const ipCountry = (profile as any)?.ip_country as string | undefined;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '85vh',
                    maxHeight: '92vh',
                    borderRadius: `${T.radius.xl}px`,
                    bgcolor: T.color.surface.page,
                    overflow: 'hidden',
                },
            }}
        >
            {/* Title bar */}
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                px: `${T.space[5]}px`, py: `${T.space[4]}px`,
                bgcolor: T.color.surface.raised,
                borderBottom: `1px solid ${T.color.surface.border}`,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[2]}px`, minWidth: 0 }}>
                    <PersonIcon sx={{ color: T.color.text.tertiary }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: T.type.size.lg, fontWeight: T.type.weight.bold, color: T.color.text.primary }}>
                            User profile
                        </Typography>
                        <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {userEmail}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} aria-label="Close" sx={{ color: T.color.text.secondary }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: `${T.space[5]}px` }}>
                {loading ? (
                    <Box>
                        <Skeleton variant="rounded" height={150} sx={{ borderRadius: `${T.radius.lg}px`, mb: `${T.space[3]}px` }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} lg={4}><Skeleton variant="rounded" height={360} sx={{ borderRadius: `${T.radius.lg}px` }} /></Grid>
                            <Grid item xs={12} lg={8}><Skeleton variant="rounded" height={360} sx={{ borderRadius: `${T.radius.lg}px` }} /></Grid>
                        </Grid>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ borderRadius: `${T.radius.md}px` }}>{error}</Alert>
                ) : userData && profile ? (
                    <Box>
                        {/* Hero */}
                        <Box sx={{
                            position: 'relative',
                            borderRadius: `${T.radius.lg}px`,
                            p: `${T.space[5]}px`,
                            mb: `${T.space[3]}px`,
                            background: `linear-gradient(135deg, ${T.color.brand[500]} 0%, ${T.color.brand[700]} 100%)`,
                            color: T.color.text.inverse,
                            boxShadow: T.shadow.md,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[4]}px`, flexWrap: 'wrap' }}>
                                <Avatar
                                    src={profile.avatar_url}
                                    sx={{ width: 96, height: 96, border: '4px solid rgba(255,255,255,0.35)', fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold, bgcolor: 'rgba(255,255,255,0.18)' }}
                                >
                                    {initialsFrom(profile.full_name, profile.email)}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold, lineHeight: T.type.lineHeight.tight }}>
                                        {profile.full_name || 'No name'}
                                    </Typography>
                                    <Typography sx={{ fontSize: T.type.size.md, opacity: 0.9, mb: `${T.space[2]}px`, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {profile.email}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: `${T.space[2]}px`, flexWrap: 'wrap' }}>
                                        <Pill tone={STATUS_TONE[currentStatus] || STATUS_TONE.active} />
                                        {profile.age != null && (
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: `${T.space[1]}px`, px: `${T.space[2]}px`, py: '2px', borderRadius: `${T.radius.pill}px`, bgcolor: 'rgba(255,255,255,0.2)', fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold }}>
                                                {profile.age} yrs
                                            </Box>
                                        )}
                                        {profile.gender && (
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: `${T.space[1]}px`, px: `${T.space[2]}px`, py: '2px', borderRadius: `${T.radius.pill}px`, bgcolor: 'rgba(255,255,255,0.2)', fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold, textTransform: 'capitalize' }}>
                                                {profile.gender}
                                            </Box>
                                        )}
                                        {locationText && (
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: `${T.space[1]}px`, px: `${T.space[2]}px`, py: '2px', borderRadius: `${T.radius.pill}px`, bgcolor: 'rgba(255,255,255,0.2)', fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold }}>
                                                <LocationIcon sx={{ fontSize: 14 }} /> {locationText}
                                            </Box>
                                        )}
                                        {photos.length > 0 && (
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: `${T.space[1]}px`, px: `${T.space[2]}px`, py: '2px', borderRadius: `${T.radius.pill}px`, bgcolor: 'rgba(255,255,255,0.2)', fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold }}>
                                                {photos.length} photos
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                            {profile.bio && (
                                <Box sx={{ mt: `${T.space[3]}px`, p: `${T.space[3]}px`, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: `${T.radius.md}px` }}>
                                    <Typography sx={{ fontSize: T.type.size.sm, fontStyle: 'italic', opacity: 0.95 }}>
                                        “{profile.bio}”
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Status alert */}
                        {(isCurrentlyBanned || isCurrentlySuspended) && (
                            <Alert
                                severity={isCurrentlyBanned ? 'error' : 'warning'}
                                icon={<WarningIcon />}
                                sx={{ mb: `${T.space[3]}px`, borderRadius: `${T.radius.md}px` }}
                            >
                                This user is currently <strong>{currentStatus}</strong>.
                                {isCurrentlySuspended && userData.statusHistory[0]?.expires_at && (
                                    <> Suspension expires {formatDate(userData.statusHistory[0].expires_at)}.</>
                                )}
                            </Alert>
                        )}

                        {/* Quick stats */}
                        {stats && (
                            <Grid container spacing={2} sx={{ mb: `${T.space[3]}px` }}>
                                <Grid item xs={6} sm={3}>
                                    <StatTile label="Account age" value={formatAccountAge(stats.account_age_days)} />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <StatTile label="Total reports" value={stats.total_reports} tone={stats.total_reports > 3 ? 'error' : 'neutral'} />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <StatTile label="High priority" value={stats.high_priority_reports} tone={stats.high_priority_reports > 0 ? 'warning' : 'neutral'} />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <StatTile label="Admin actions" value={stats.total_admin_actions} />
                                </Grid>
                            </Grid>
                        )}

                        <Grid container spacing={3}>
                            {/* Left column */}
                            <Grid item xs={12} lg={4}>
                                <Panel>
                                    <PanelTitle icon={<PersonIcon fontSize="small" />}>Profile details</PanelTitle>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[3]}px` }}>
                                        {locationText && <DetailRow icon={<LocationIcon fontSize="small" />} label="Location" value={locationText} />}
                                        {ipCountry && <DetailRow icon={<GlobeIcon fontSize="small" />} label="IP country" value={ipCountry} />}
                                        {profile.gender && <DetailRow icon={<GenderIcon fontSize="small" />} label="Gender" value={<span style={{ textTransform: 'capitalize' }}>{profile.gender}</span>} />}
                                        {profile.age != null && <DetailRow icon={<AgeIcon fontSize="small" />} label="Age" value={`${profile.age} years`} />}
                                    </Box>

                                    <Box sx={{ height: '1px', bgcolor: T.color.surface.border, my: `${T.space[4]}px` }} />

                                    <Typography sx={{ fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold, color: T.color.text.tertiary, textTransform: 'uppercase', letterSpacing: 0.5, mb: `${T.space[2]}px` }}>
                                        Account information
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                                        {[
                                            { icon: <CalendarIcon fontSize="small" />, label: 'Member since', value: formatDate(profile.created_at) },
                                            { icon: <HistoryIcon fontSize="small" />, label: 'Last updated', value: formatDate(profile.updated_at) },
                                            { icon: <BadgeIcon fontSize="small" />, label: 'User ID', value: <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: T.type.size.xs }}>{profile.id}</span> },
                                        ].map((row) => (
                                            <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: `${T.space[3]}px` }}>
                                                <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, whiteSpace: 'nowrap' }}>{row.label}</Typography>
                                                <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.primary, fontWeight: T.type.weight.medium, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {row.value}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* Report breakdown */}
                                    {stats && stats.total_reports > 0 && (
                                        <>
                                            <Box sx={{ height: '1px', bgcolor: T.color.surface.border, my: `${T.space[4]}px` }} />
                                            <Typography sx={{ fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold, color: T.color.text.tertiary, textTransform: 'uppercase', letterSpacing: 0.5, mb: `${T.space[2]}px` }}>
                                                Report breakdown
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${T.space[2]}px` }}>
                                                <Pill tone={{ ...STATUS_TONE.pending, label: `${stats.pending_reports} pending` }} />
                                                <Pill tone={{ ...STATUS_TONE.resolved, label: `${stats.resolved_reports} resolved` }} />
                                                <Pill tone={{ ...STATUS_TONE.dismissed, label: `${stats.dismissed_reports} dismissed` }} />
                                            </Box>
                                        </>
                                    )}
                                </Panel>
                            </Grid>

                            {/* Right column */}
                            <Grid item xs={12} lg={8}>
                                {/* Engagement */}
                                <EngagementPanel engagement={userData.engagement} sx={{ mb: `${T.space[3]}px` }} />

                                {/* Photos */}
                                {photos.length > 0 && (
                                    <Panel sx={{ mb: `${T.space[3]}px` }}>
                                        <PanelTitle icon={<PhotoIcon fontSize="small" />} count={photos.length}>Photos</PanelTitle>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                                            gap: `${T.space[2]}px`,
                                            maxHeight: 420, overflowY: 'auto',
                                        }}>
                                            {photos.map((photo, index) => (
                                                <Box
                                                    key={index}
                                                    onClick={() => handlePhotoClick(index)}
                                                    sx={{
                                                        position: 'relative', cursor: 'pointer',
                                                        aspectRatio: '1 / 1',
                                                        borderRadius: `${T.radius.md}px`, overflow: 'hidden',
                                                        bgcolor: T.color.surface.sunken,
                                                        '&:hover .expand-overlay': { opacity: 1 },
                                                    }}
                                                >
                                                    <img
                                                        src={photo}
                                                        alt={`User photo ${index + 1}`}
                                                        loading="lazy"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                    />
                                                    <Box className="expand-overlay" sx={{
                                                        position: 'absolute', inset: 0,
                                                        bgcolor: 'rgba(0,0,0,0.55)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        opacity: 0, transition: `opacity ${T.motion.fast}`,
                                                    }}>
                                                        <ExpandIcon sx={{ color: '#fff', fontSize: 32 }} />
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Panel>
                                )}

                                {/* Reports against user */}
                                <Panel sx={{ mb: `${T.space[3]}px` }}>
                                    <PanelTitle icon={<ReportIcon fontSize="small" />} count={userData.reports.length}>Reports against user</PanelTitle>
                                    {userData.reports.length === 0 ? (
                                        <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.tertiary, textAlign: 'center', py: `${T.space[4]}px` }}>
                                            No reports found
                                        </Typography>
                                    ) : (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ '& th': { bgcolor: T.color.surface.sunken, color: T.color.text.secondary, fontWeight: T.type.weight.semibold, fontSize: T.type.size.xs, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${T.color.surface.border}` } }}>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell>Reporter</TableCell>
                                                        <TableCell>Priority</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell>Date</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {userData.reports.slice(0, 8).map((report: any) => (
                                                        <TableRow key={report.id} sx={{ '& td': { borderBottom: `1px solid ${T.color.surface.border}`, fontSize: T.type.size.sm, verticalAlign: 'top', py: `${T.space[2]}px` } }}>
                                                            <TableCell sx={{ maxWidth: 220 }}>
                                                                <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary }}>
                                                                    {formatReportType(report.report_type)}
                                                                </Typography>
                                                                {report.content && (
                                                                    <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                                        {report.content}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell sx={{ color: T.color.text.secondary, maxWidth: 160 }}>
                                                                <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {report.reporter_name || report.reporter_email || 'Unknown'}
                                                                </Typography>
                                                                {report.reporter_name && report.reporter_email && (
                                                                    <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {report.reporter_email}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{report.priority ? <Pill tone={PRIORITY_TONE[String(report.priority).toLowerCase()] || PRIORITY_TONE.medium} /> : '—'}</TableCell>
                                                            <TableCell><Pill tone={STATUS_TONE[String(report.status || 'pending').toLowerCase()] || STATUS_TONE.pending} /></TableCell>
                                                            <TableCell sx={{ color: T.color.text.secondary, whiteSpace: 'nowrap' }}>{formatDate(report.created_at)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                    {userData.reports.length > 8 && (
                                        <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, mt: `${T.space[2]}px`, textAlign: 'center' }}>
                                            Showing 8 of {userData.reports.length} reports
                                        </Typography>
                                    )}
                                </Panel>

                                {/* Admin action history */}
                                <Panel sx={{ mb: `${T.space[3]}px` }}>
                                    <PanelTitle icon={<GavelIcon fontSize="small" />} count={userData.adminActions?.length || 0}>Admin action history</PanelTitle>
                                    {(!userData.adminActions || userData.adminActions.length === 0) ? (
                                        <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.tertiary, textAlign: 'center', py: `${T.space[4]}px` }}>
                                            No admin actions taken
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                                            {userData.adminActions.slice(0, 6).map((action: any) => (
                                                <Box key={action.id} sx={{ p: `${T.space[3]}px`, bgcolor: T.color.surface.sunken, borderRadius: `${T.radius.md}px`, borderLeft: `3px solid ${T.color.brand[400]}` }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: `${T.space[2]}px`, mb: `${T.space[1]}px` }}>
                                                        <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary }}>
                                                            {formatActionType(action.action_type)}
                                                            {action.related_report_type ? ` · ${formatReportType(action.related_report_type)}` : ''}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, whiteSpace: 'nowrap' }}>{formatDate(action.created_at)}</Typography>
                                                    </Box>
                                                    <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary, mb: `${T.space[1]}px` }}>
                                                        By {action.admin_name || action.admin_email}
                                                    </Typography>
                                                    {action.reason && (
                                                        <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.primary }}>{action.reason}</Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Panel>

                                {/* Status history */}
                                <Panel>
                                    <PanelTitle icon={<HistoryIcon fontSize="small" />} count={userData.statusHistory.length}>Status history</PanelTitle>
                                    {userData.statusHistory.length === 0 ? (
                                        <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.tertiary, textAlign: 'center', py: `${T.space[4]}px` }}>
                                            No status changes recorded
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                                            {userData.statusHistory.slice(0, 6).map((record: any) => {
                                                const tone = STATUS_TONE[String(record.status || 'active').toLowerCase()] || STATUS_TONE.active;
                                                return (
                                                    <Box key={record.id} sx={{ p: `${T.space[3]}px`, bgcolor: T.color.surface.sunken, borderRadius: `${T.radius.md}px`, borderLeft: `3px solid ${tone.dot || T.color.text.tertiary}` }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: `${T.space[2]}px`, mb: `${T.space[1]}px` }}>
                                                            <Pill tone={tone} />
                                                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, whiteSpace: 'nowrap' }}>{formatDate(record.created_at)}</Typography>
                                                        </Box>
                                                        <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary, mb: `${T.space[1]}px` }}>
                                                            By {record.admin_name || record.admin_email}
                                                        </Typography>
                                                        {record.reason && (
                                                            <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.primary }}>{record.reason}</Typography>
                                                        )}
                                                        {record.expires_at && (
                                                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, mt: `${T.space[1]}px` }}>
                                                                Expires {formatDate(record.expires_at)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Panel>
                            </Grid>
                        </Grid>
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.tertiary }}>
                        No profile data available
                    </Typography>
                )}
            </DialogContent>

            {/* Footer */}
            <Box sx={{
                display: 'flex', justifyContent: 'flex-end',
                px: `${T.space[5]}px`, py: `${T.space[3]}px`,
                bgcolor: T.color.surface.raised,
                borderTop: `1px solid ${T.color.surface.border}`,
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        textTransform: 'none', fontWeight: T.type.weight.semibold,
                        borderColor: T.color.surface.borderStrong, color: T.color.text.primary,
                        '&:hover': { borderColor: T.color.brand[400], bgcolor: T.color.brand[50] },
                    }}
                >
                    Close
                </Button>
            </Box>

            {/* Photo Viewer Modal */}
            <Modal
                open={photoViewerOpen}
                onClose={handlePhotoViewerClose}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Box sx={{ position: 'relative', maxWidth: '92vw', maxHeight: '92vh', outline: 'none' }}>
                    {photos[currentPhotoIndex] && (
                        <>
                            <Tooltip title="Close (Esc)">
                                <IconButton
                                    onClick={handlePhotoViewerClose}
                                    sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', zIndex: 2, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>

                            {photos.length > 1 && (
                                <>
                                    <IconButton
                                        onClick={handlePrevPhoto}
                                        aria-label="Previous photo"
                                        sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', zIndex: 2, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                    >
                                        <PrevIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={handleNextPhoto}
                                        aria-label="Next photo"
                                        sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', zIndex: 2, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                    >
                                        <NextIcon />
                                    </IconButton>
                                </>
                            )}

                            <img
                                src={photos[currentPhotoIndex]}
                                alt={`Photo ${currentPhotoIndex + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'contain', maxWidth: '92vw', maxHeight: '92vh', borderRadius: T.radius.md }}
                            />

                            {photos.length > 1 && (
                                <Box sx={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', px: `${T.space[3]}px`, py: `${T.space[1]}px`, borderRadius: `${T.radius.pill}px`, zIndex: 2 }}>
                                    <Typography sx={{ fontSize: T.type.size.xs, fontVariantNumeric: 'tabular-nums' }}>
                                        {currentPhotoIndex + 1} of {photos.length}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Modal>
        </Dialog>
    );
};

export default UserProfileView;
